/**
 * use-project-group-by.ts
 *
 * Manages the group-by selection for the Projects list view.
 * Supports multiple simultaneous group-by fields (ordered array).
 * State is persisted in sessionStorage so it survives navigation within
 * the same browser tab but resets on a fresh load.
 */

import { useState, useCallback } from 'react';
import type { GroupById } from '@/lib/project-group-by-utils';

const STORAGE_KEY = 'projects-group-by-v2';

function readStored(): GroupById[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as GroupById[];
    }
  } catch {
    // ignore
  }
  return [];
}

export function useProjectGroupBy() {
  const [groupBy, setGroupByState] = useState<GroupById[]>(readStored);

  const setGroupBy = useCallback((value: GroupById[]) => {
    setGroupByState(value);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      // ignore storage errors
    }
  }, []);

  /** Toggle a single field in/out of the group-by array */
  const toggleGroupBy = useCallback((field: GroupById) => {
    setGroupByState((prev) => {
      const next = prev.includes(field)
        ? prev.filter((f) => f !== field)
        : [...prev, field];
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  return { groupBy, setGroupBy, toggleGroupBy };
}
